// Layer 1 (Data Schema): the Module 3 advisory loop as the word doc names it —
// VariationEvent → Case → RecommendedAction → CaseOutcome → FMEAUpdateReview —
// plus Alarm, kept as a *separate* object from Case (word-doc Module 1 §7:
// reuse the plant's P1–P4 alarm philosophy; an advisory is routed, confidence-
// scored and action-carrying, an alarm is a threshold crossing).
//
// Case model — unified advisory spine (F11 #45).
// advisory-issue.ts (tim-scope reconciliation R3 / R4 / R10, 2026-08-19).
// Nothing consumes these types yet — consumers migrate under reconcile.T13.

import type { IssueSeverity, PriorityUrgency, PlantCaseKind, ControlMode } from './advisory-issue';
import type { PlantRoleId } from './plant-role';
import type { RulePriority } from './diagnostic-rule';
import type { UnitOpType } from './unit-operation';
import type { SeatId } from './seat';
import type { Persona } from '@/shared/types';

// ---------------------------------------------------------------------------
// Case state machine — word-doc Module 3 §4 workflow, state vocabulary per
// tim-scope advisory-loop.md §3.1.
// ---------------------------------------------------------------------------

export type CaseState =
  | 'detected'            // VariationEvent raised against spec / baseline
  | 'matched'             // rules scored; control mode resolved FIRST
  | 'routed'              // zero-touch: Immediate Response Action sent to the Responsible role
  | 'evidence-requested'  // ask: one named, discriminating piece of evidence outstanding
  | 'escalated'           // no loose match anywhere in the FMEA → Metallurgist
  | 'diagnosed'           // cause established by whoever resolved it
  | 'outcome-documented'  // CaseOutcome written — a PROPOSED FMEA change, never live
  | 'fmea-review'         // causeCategory-routed reviewer(s) sense-checking the proposal
  | 'approved'            // Plant Manager gate passed → DiagnosticRule live
  | 'rejected'            // gate refused → no FMEA change; case still closes
  | 'closed';

/** The three Module 3 §4 branches. `kind` on the case keeps the §6 worked-example tag. */
export type CaseBranch = 'zero-touch' | 'ask' | 'escalate';

/** Cause category on the outcome decides who reviews (word-doc §M3 §4; feed/ore per §6.5). */
export type CauseCategory =
  | 'metallurgical-process'
  | 'mechanical-electrical'
  | 'spans-both'
  | 'feed-ore';

/** The two review seats named by the doc; in v1 they are capabilities carried by
 *  the Metallurgist and Maintenance Supervisor personas (reconciliation R11 / D8). */
export type ReviewSeat = 'senior-metallurgist' | 'senior-reliability-engineer';

export type ReviewStatus = 'pending' | 'approved' | 'rejected';

// ---------------------------------------------------------------------------
// VariationEvent — a detected departure from spec or baseline (A §M3).
// ---------------------------------------------------------------------------

export type MatchedRule = {
  ruleId: string;
  /** Deck value, not a computed score — the doc lists confidence scoring as still missing (A §M3 §7). */
  confidencePct: number;
};

export type VariationEvent = {
  id: string;
  /** Subprocess id (`sp-*`) the departure is anchored to. */
  subprocessId: string;
  /** Monitored variable (`mv-*`) this event watches — required for seeded `ve-*` cases. */
  variableId?: string;
  /** Optional stream id when the departure is a material-quality one. */
  streamId?: string;
  detectedAt: string;
  description: string;
  /** Layer-A magnitude, source wording preserved ("−3.3% vs design", "8 days running"). */
  magnitude: string;
  matchedRules: readonly MatchedRule[];
};

// ---------------------------------------------------------------------------
// Case — the object that moves through the workflow; the spine of the product.
// ---------------------------------------------------------------------------

export type EvidenceKind = 'lab-sample' | 'visual' | 'loop-state';

export type EvidenceRequest = {
  id: string;
  kind: EvidenceKind;
  /** The ask is targeted: Field Operator for a sample / a walk-out look, CRO for loop state / a lab request (A §M3 §1). */
  target: PlantRoleId;
  text: string;
  status: 'open' | 'answered';
  answer?: string;
  answeredAt?: string;
};

export type ControlModeCheck = {
  /** Loop tag as the plant names it (LIC4010, AIC5002). */
  tag: string;
  mode: ControlMode;
  /** Who confirmed it and when — the CRO's contribution on the money path. */
  confirmedBy?: PlantRoleId;
  confirmedAt?: string;
};

export type ConfidenceDiscount = {
  /** Sample-point id whose MISS discounts the grade call (A §M1 §7). */
  samplePointId: string;
  reason: string;
};

/** Delta tone follows *badness*, not arrow direction (corpus map V6 — the deck colours ▲ green even when up is bad). */
export type HeadlineTone = 'ok' | 'warn' | 'bad' | 'info' | 'neutral';

/**
 * The finding card's KPI readout, exactly as deck slide 05 draws it:
 * "Recovery (24h avg): 87.0% (Design: 90.0%) ▼ 3.3% vs design".
 * A headline is a KpiValue + its design Target flattened for one card
 * (target.ts, R6) — values are carried verbatim, never recomputed, so the S1
 * design-basis conflict rides on `provenance`, not on arithmetic.
 * (Module 3 second pass, corpus map G1.)
 */
export type CaseHeadline = {
  /** Metric label as the card prints it ("Recovery (24h avg)", "Cyclone overflow P80"). */
  label: string;
  value: string;
  /** Comparison basis shown in parentheses — "Design" for three cards, "30d ago" for the arsenic trend (V7). */
  basis?: { label: string; value: string };
  delta: { text: string; direction: 'up' | 'down' | 'flat'; tone: HeadlineTone };
  /** Deck page / doc section the numbers came from, or `[syn]`. */
  provenance: string;
};

/**
 * Who is handling the case right now — the Digital TARP "live view of who is
 * handling it" (prior-art-mipac §4, #55). A person, where `assignedTo` is a role.
 */
export type CaseHandler = {
  name: string;
  role: PlantRoleId;
  /** When they picked it up (frozen-clock time). */
  since: string;
};

/**
 * Escalate-on-silence: no response by `at` → the case escalates to `escalatesTo`
 * (Digital TARP steal, #55). The window has no source — every seeded value is `[syn]`.
 * Superseded on ladder cases by `ladder` + `ladderRead()` (tarp.A6).
 */
export type CaseRespondBy = {
  at: string;
  escalatesTo: PlantRoleId;
};

/** Sampling-artefact gate before an assay signal may escalate (emails.A18). */
export type SamplingArtefactCheck = {
  text: string;
  labDueAt: string;
};

/** Equipment excursion linked to the process KPI it precedes (emails.A20 #335). */
export type CaseAffects = {
  readingId: string;
  /** Fixed lead for ordinary cases; omitted when `leadSource` derives it. */
  leadHours?: number;
  /** Use the BM01 bearing trend's time to its Reading specification limit. */
  leadSource?: 'bearing-limit-trend';
  basis: string;
};

export type Case = {
  id: string;
  /** Deck-facing code where one exists (ZN-REC, AG-CONC …). */
  code?: string;
  variationEventId?: string;
  /** Set when the case was spawned off a DCS alarm rather than a VariationEvent. */
  alarmId?: string;
  /**
   * Direct monitored-variable link (`mv-*`) for a case the corpus positions on
   * a variable without a seeded variation event or alarm (`case-zn-rgh-ph` on
   * the AT-150 rougher-feed pH). The authoritative answer when present.
   */
  variableId?: string;
  title: string;
  circuit: string;
  state: CaseState;
  branch: CaseBranch;
  /** Word-doc §6 worked-example tag; orthogonal to `branch`. */
  kind?: PlantCaseKind;
  /** Persona-level assignee (the seven deck roles); DiagnosticRule.responsible stays the FMEA's coarser Responsible column. */
  assignedTo: PlantRoleId;
  /** Display severity — the deck's language on the glass. */
  severity: IssueSeverity;
  /** Deck value; `undefined` for data-quality / review-only cases — omit the chip. */
  aiConfidencePct?: number;
  confidenceDiscount?: ConfidenceDiscount;
  /** Provisional flag per A §M1 §7 latency tiers: raised on the on-line signal, resolved when the lab result lands. */
  provisional?: { labDueAt: string };
  /** Layer-A facts as the AI card states them (deck prose, each clause traceable to a seeded field). */
  observation: string;
  /** Layer-B phrasing of a candidate cause — hedged where the rule is a candidate, not a finding. */
  causeCandidate: string;
  /** Met Diagnostics IF → THEN as readable text (hybrid format, prose half). */
  ifThen: string;
  controlModeCheck?: ControlModeCheck;
  evidenceRequests: readonly EvidenceRequest[];
  /** Slide-05 KPI readout; absent on the word-doc §6 worked examples, which carry no deck card. */
  headline?: CaseHeadline;
  /** KPI or value-chain node this case feeds when no reading label is linked. */
  feedNodeLabel?: string;
  /** Who has it now (person); absent when the case is closed or unpicked. */
  handler?: CaseHandler;
  /** Escalate-on-silence deadline; absent when nothing is awaited. */
  respondBy?: CaseRespondBy;
  /**
   * The control room's next step on a case that carries no ladder and no open
   * ask (AIR-VALVE's zero-touch valve walk-out). The landing reads it before
   * its diagnosis fallback, so a step is a step, never the title restated.
   */
  nextStep?: string;
  /** Position on an escalation ladder — `respondBy` is derived from this when set. */
  ladder?: import('./escalation-ladder').CaseLadderPosition;
  /** Assay signals: rule out sampling artefact before full escalation (emails.A18). */
  samplingArtefactCheck?: SamplingArtefactCheck;
  /** Process KPI the equipment excursion threatens, with lead time (emails.A20 #335). */
  affects?: CaseAffects;
  /** Names the committed analysis envelope this case reads in the drawer; absent on every other case. */
  analysisRun?: {
    fixture: string;
    primarySignalRole?: string;
    expectationRole?: string;
    recordedAgent?: boolean;
    /** Equipment handoffs use the fixture's clocked diagnosis/action, not authored agent times. */
    milestonesFromFrames?: boolean;
  };
  /** Human checks for a controlled stop; hours and value are read from the fixture. */
  stopPlan?: CaseStopPlan;
  /** How long the response takes to show and when the case may close (M3 worked example; reconciliation A4). */
  recovery?: CaseRecoveryCriterion;
  /**
   * K13c: what this case's own diagnosis teaches the FMEA, drawn as "+" lines on
   * the living-FMEA row and made live only by the Senior Metallurgist gate.
   * Never typed on the glass; a case with no lesson omits both.
   */
  learnedCause?: string;
  learnedEarlyWarning?: string;
  /** When a metallurgist or agent established the finding, for clocked flow reads. */
  diagnosedAt?: string;
  raisedAt: string;
  resolvedAt?: string;
};

/** A controlled stop handed to Maintenance (D16, reconciliation A6): the draft work order,
 *  the checks before release and who decides the stop. Hours and US$ come from the fixture. */
export type CaseStopPlan = {
  workOrderId: string;
  humanCheck: string;
  closeCriterion: string;
  /** `at` is the stop's start clock when seeded; release checks dated to the stop day read it. */
  window: { at?: string; label: string; shift: string };
  /** The draft work order's fields (A6): asset, criticality and spares kit. */
  workOrder?: { asset: string; criticality: string; kit: string };
  /** What must clear before the work order is released, in the order the crew reads it. */
  releaseChecks?: readonly CaseReleaseCheck[];
  /** The stop is the GM's escalation, confirmed by the Maintenance Superintendent (A6). */
  stopDecision?: { decides: string; confirms: string; due: string };
};

/** One check before release: when (`14:00`, `now`, `then`, or the stop day read from the window), the lead words, the rest and an optional basis. */
export type CaseReleaseCheck =
  & { lead: string; detail: string; basis?: string }
  & ({ at: string; onStopDay?: never } | { at?: never; onStopDay: true });

/** Time to recover after the action lands, and the close criterion the action gate names. */
export type CaseRecoveryCriterion = {
  /** Hours from the action landing to the process responding. */
  lagHours: { min: number; max: number };
  /** Consecutive shift assays at target before the case closes. */
  closeAfterShiftAssays: number;
};

// ---------------------------------------------------------------------------
// RecommendedAction — the deck's numbered priority list, per case, with its own
// urgency + status (ranking crosses cases, so it is not inherited from Case).
// ---------------------------------------------------------------------------

export type ActionStatus = 'proposed' | 'approved' | 'rejected' | 'done';

/**
 * Approve and reject are the same event with different answers (route-reads
 * `paul-feedback.A8` #268, slice 2 hard rule 1) — one optional object, never
 * two parallel field pairs. `seat`, not role: the record of who decided
 * names the seat that held it (`PRIMARY_SEAT_BY_ROLE` maps role → seat,
 * `seatLabelById` in `shared/org.ts` renders it). `reason` is required on a
 * refusal — a refusal with no reason is not a decision, it is a shrug.
 */
export type ActionDecision = {
  seat: SeatId;
  at: string;
  reason?: string;
};

/** A refusal is a decision whose reason is not optional — a refusal with no
 *  reason is a shrug, not a decision (slice 2 hard rule 1). */
export type ActionRefusal = ActionDecision & { reason: string };

type RecommendedActionBase = {
  id: string;
  caseId: string;
  /** Position in the merged plant-wide priority list (deck slide 5). `null` = a per-case
   *  secondary action that is not on the merged list (deck finding actions 2–3). */
  sequence: number | null;
  urgency: PriorityUrgency;
  text: string;
  /** Downstream reading checked before this action may render (emails.A6 #323). */
  constraint?: { readingId: string };
  /** When the proposed action entered the case, before any approval decision. */
  proposedAt?: string;
};

/**
 * `RecommendedAction` is a discriminated union on `status`, not a bare
 * `status` field beside independently-optional `decision?` / `implementedAt?`
 * (fixer round after the uptake review, MUST-FIX 7). `'approved'` and
 * `'rejected'` cannot exist without a `decision`; `'rejected'` cannot exist
 * without a `reason` on it; `'done'` cannot exist without both a `decision`
 * and an `implementedAt` — the type is the enforcement docs/schema/case.md
 * describes, not a runtime check nothing was actually running. The two
 * seeded `'done'` rows (`bunker-hill-advisory-seed.ts`) already satisfy this
 * shape; `AdvisoryEngine.applyRecommendedActions` is the one place that
 * constructs a `RecommendedAction` from session state and does the same.
 */
export type RecommendedAction =
  | (RecommendedActionBase & {
      status: 'proposed';
      decision?: undefined;
      implementedAt?: undefined;
    })
  | (RecommendedActionBase & {
      status: 'approved';
      /** The named seat's decision to accept — no reason required. */
      decision: ActionDecision;
      implementedAt?: undefined;
    })
  | (RecommendedActionBase & {
      status: 'rejected';
      /** The named seat's refusal — reason required (a refusal with no
       *  reason is not a decision, it is a shrug). */
      decision: ActionRefusal;
      implementedAt?: undefined;
    })
  | (RecommendedActionBase & {
      status: 'done';
      decision: ActionDecision;
      /**
       * When the accepted change was actually made on the plant — a third
       * time, and never the decision time (`paul-feedback.A8` hard rule 3:
       * decision latency raised→decided and implementation latency
       * decided→implemented are two quantities, never summed, never
       * substituted).
       */
      implementedAt: string;
    });

// ---------------------------------------------------------------------------
// CaseOutcome — documented resolution; ALWAYS a proposed FMEA change, never live.
// ---------------------------------------------------------------------------

export type CaseOutcome = {
  id: string;
  caseId: string;
  diagnosedCause: string;
  causeCategory: CauseCategory;
  evidence: string;
  actionTaken: string;
  effectiveness?: string;
  /** Present when the outcome proposes a rule (new row / narrower condition / adjusted likelihood). */
  proposedRuleId?: string;
};

/** Effectiveness read — re-watching the watched signal after an approved action (construct.R10). */
export type EffectivenessState =
  | 'awaiting-action'
  | 'watching'
  | 'improving'
  | 'no-change'
  | 'worsening'
  | 'resolved'
  | 'unobservable';

export type EffectivenessRead = {
  caseId: string;
  signalRole: import('./tag-mapping').SignalRole | null;
  variableId: string | null;
  /** Reading label for the watched signal. */
  label: string;
  state: EffectivenessState;
  actionAt?: import('./reading-series').SeriesTime;
  /** Clock string via `formatClockShort`. */
  since?: string;
  pointsSince?: number;
  before?: { value: number; trend: 'rising' | 'falling' | 'stable' };
  after?: { value: number; trend: 'rising' | 'falling' | 'stable' };
  sentence: string;
};

// ---------------------------------------------------------------------------
// FMEAUpdateReview — routed by causeCategory to reviewer(s), then ONE approver.
// ---------------------------------------------------------------------------

export type ProposedChangeKind =
  | 'new-row'
  | 'narrower-condition'
  | 'adjusted-likelihood'
  | 'band-change';

export type FMEAUpdateReview = {
  id: string;
  caseOutcomeId?: string;
  causeCategory: CauseCategory;
  /** Both seats when the cause spans both (A §M3 §4). */
  reviewSeats: readonly ReviewSeat[];
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  reviewStatus: ReviewStatus;
  /** "A deliberate, single accountable gate regardless of how many reviewers were involved." */
  approvalStatus: ReviewStatus;
  /** Optional review-specific approver lens; absent keeps the default `fmea:approve` gate. */
  approvalPersonas?: readonly Persona[];
  /** Optional glass label for an approval gate scoped to named personas. */
  approvalLabel?: string;
  approvedBy?: string;
  approvedAt?: string;
  proposedChange: {
    kind: ProposedChangeKind;
    summary: string;
    ruleId?: string;
    bandChange?: {
      controlPlanRowId: string;
      fromZoneId: string;
      toZoneId: string;
      proposedBand: import('./reading').Band;
      basis: string;
    };
  };
};

// ---------------------------------------------------------------------------
// FailureMode — FMEA row authored against a unit-op (Case spine).
// TYPE, never a site instance (A arch §5). RPN stays stored, not derived.
// ---------------------------------------------------------------------------

export type FailureMode = {
  id: string;
  unitOpType: UnitOpType | 'plant-wide';
  circuit: string;
  failureMode: string;
  cause: string;
  effect: string;
  s: number;
  o: number;
  d: number;
  rpn: number;
  /** Multi-variable early-warning signature, source wording preserved. */
  earlyWarning: string;
  /** Live rows are what the engine matches against; proposed rows exist only via an FMEAUpdateReview. */
  status: 'live' | 'proposed';
  /** rules2 (paul-call.A6, #250) - which grade/recovery outcome the effect text names.
   * Additive; no provenance field exists on this type, so an invented S/O/D still
   * carries [syn] inline in cause/effect rather than here. Set where the effect
   * text makes the outcome obvious; omitted rows are unclassified, not zero. */
  outcome?: 'recovery' | 'grade' | 'both';
};

// ---------------------------------------------------------------------------
// Alarm — DCS-originated; the plant's own P1–P4 philosophy stored, display
// severity derived (lossy in one direction, so never the reverse).
// ---------------------------------------------------------------------------

export type AlarmState = 'active' | 'acked';

export type Alarm = {
  id: string;
  /** One code per alarm, seeded from the Ops Supervisor set (S12): ZN-PH-01, AG-CONC, BM-BRG, PUMP-04. */
  code: string;
  priority: RulePriority;
  message?: string;
  /** Loop / instrument tag where the deck names one. */
  tag?: string;
  raisedAt: string;
  state: AlarmState;
  /** A zero-touch case spawned off this alarm (ZN-PH-01 → the lime-SP immediate response). */
  spawnedCaseId?: string;
  /** What raised this row — modelled explicitly, never inferred from whether
   * `tag` happens to be set (review `priority-likelihood`, M7). `'dcs'` is a
   * plant alarm the DCS itself raised (ZN-PH-01, BM-BRG, PUMP-04 in the
   * seed); `'advisory'` is this product's own diagnostic hypothesis wearing
   * an alarm's shape (AG-CONC — its own message says "(advisory, see
   * case)"). Only a `'dcs'` alarm can shield a variable from the CRO's
   * excursion queue (`DiagnosisEngine.alarmCoveredVariableIds`) — an
   * advisory row is not independent coverage of anything, by construction:
   * it is coverage by this product's own prior output. */
  origin: 'dcs' | 'advisory';
};

/** P1–P4 → the deck's HIGH / MED / LOW display severity. Derived, never stored. */
export const ALARM_DISPLAY_SEVERITY: Record<RulePriority, 'high' | 'med' | 'low'> = {
  P1: 'high',
  P2: 'high',
  P3: 'med',
  P4: 'low',
};

export type SampleRequestMode = 'rush' | 'campaign';
export type SampleCampaign = {
  intervalMin: number;
  hours: number;
};
export type SampleRequest = {
  divergenceId: string;
  samplePointId?: string;
  requestedAt: string;
  requestedBy: PlantRoleId;
  mode: SampleRequestMode;
  campaign?: SampleCampaign;
};
